const HYDRATION_CACHE = {}
const HYDRATION_SMALL_CACHE = {}
const HYDRATION_MEDIUM_CACHE = {}
const HYDRATION_QUIZ_CACHE = {}
const SKELETON_ATTRUBUTES = [
  'data-product-href',
  'data-product-image-primary',
  'data-product-image-secondary',
  'data-product-title',
  'data-product-description',
  'data-product-price',
]
class ProductCard extends HHBaseElement {
  constructor() {
    super()
    this._hydrated = !this.shouldHydrate
    this._selectItems = this._selectItems.bind(this)
    this._trackQuickShopToggle = this._trackQuickShopToggle.bind(this)
    this._notifyMeToggle = this._notifyMeToggle.bind(this)
    this._handleInCartToggle = this._handleInCartToggle.bind(this)
    this._skeletonTemplate = document.getElementById('skeleton-product-card')
  }

  get productHandle() {
    return this.getAttribute('product-handle')
  }

  get sectionHeadline() {
    return this.getAttribute('data-section-headline')
  }

  get cardSize() {
    return this.getAttribute('card-size') ?? ''
  }

  get shouldHydrate() {
    return this.hasAttribute('hydrate')
  }

  get variantId() {
    return this.getAttribute('data-id') || ''
  }

  static get observedAttributes() {
    return ['product-handle']
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (
      name === 'product-handle' &&
      newValue !== oldValue &&
      newValue &&
      this.shouldHydrate
    ) {
      this._hydrated = false
      this._hydrate()
    }
  }

  connectedCallback() {
    if (this.shouldHydrate) {
      return this._hydrate()
    }
    this._selectItems()
    this._attachListeners()
  }

  disconnectedCallback() {
    this._removeListeners()
  }

  async _hydrate() {
    const cacheVar =
      this.cardSize === 'small'
        ? HYDRATION_SMALL_CACHE
        : this.cardSize === 'medium'
        ? HYDRATION_MEDIUM_CACHE
        : this.cardSize === 'quiz'
        ? HYDRATION_QUIZ_CACHE
        : HYDRATION_CACHE
    const viewParam = this.cardSize
      ? `product-card-${this.cardSize}`
      : 'product-card'

    if (cacheVar[this.productHandle]) {
      this.innerHTML = cacheVar[this.productHandle]
    } else {
      if (!this.cardSize) this._renderSkeleton()
      let url = `${window.Shopify.routes.root}products/${this.productHandle}?view=${viewParam}`
      if (this.variantId) {
        url += `&variant=${this.variantId}`
      }
      const request = await fetch(url)
      if (!request.ok) return this.remove() // May return 404 if product is unavailable
      const container = document.createElement('div')
      container.innerHTML = await request.text()
      const productCardNode = container.querySelector(
        viewParam.replace('-quiz', '')
      )
      if (!productCardNode) return this.remove() // May return empty div if variant is unavailable
      cacheVar[this.productHandle] = productCardNode.innerHTML
      this.innerHTML = cacheVar[this.productHandle]
    }

    this._hydrated = true
    this._handleHydration()
  }

  _renderSkeleton() {
    if (!this._skeletonTemplate) return null
    const contents = this._skeletonTemplate.cloneNode(true)
    this.innerHTML = contents.innerHTML
    SKELETON_ATTRUBUTES.forEach((attr) => {
      const data = this.getAttribute(attr)
      if (!!data) {
        const targets = Array.from(this.querySelectorAll(`[${attr}]`))
        targets.forEach((target) => {
          if (target.tagName === 'ADAPTIVE-IMAGE') {
            target.setAttribute('data-image', data)
          } else if (target.tagName === 'A') {
            target.setAttribute('href', data)
          } else {
            target.innerHTML = data
          }
        })
      }
    })
  }

  _handleHydration() {
    this._selectItems()
    this._removeListeners()
    this._attachListeners()
    this.classList.add('hydrated')
    this._setURLPostfix()
  }

  _setURLPostfix() {
    const urlPostfix = this.getAttribute('url-postfix')

    if (!!urlPostfix) {
      try {
        const link = this.querySelector('.js-product-card-link')
        if (link && !!link.href) {
          const href = new URL(link.href)
          href.searchParams.append(...urlPostfix.split('='))
          link.href = href.toString()
        }
      } catch (error) {
        console.error(error)
      }
    }
  }

  _selectItems() {
    this.quickShopToggle = this.querySelector('.js-toggle-quick-shop')
    this.notifyMeToggle = this.querySelector('.js-toggle-notify-me')
    this.inCartToggle = this.querySelector('.js-in-cart-toggle')
  }

  _attachListeners() {
    if (this.quickShopToggle) {
      this.quickShopToggle.addEventListener('click', this._trackQuickShopToggle)
    }

    if (this.notifyMeToggle) {
      this.notifyMeToggle.addEventListener('click', this._notifyMeToggle)
    }

    if (this.inCartToggle) {
      this.inCartToggle.addEventListener('click', this._handleInCartToggle)
    }
  }

  _trackQuickShopToggle() {
    this.triggerEvent('click::quick-shop-toggle', {
      product_id: this.getAttribute('data-id'),
      variant_id: this.quickShopToggle.dataset.value,
    })
  }

  _notifyMeToggle() {
    const selectedVariantId = this.notifyMeToggle.dataset.variantId
    this.product = this.decodeProduct()
    const hexcode = this.querySelector('.js-selected-variant-hex')
    const swatchImageInput = this.querySelector('.js-selected-variant-swatch-image')

    let selectedItem = null
    this.product.variants.forEach((item) => {
      if (Number(selectedVariantId) === item.id) {
        selectedItem = item
      }
    })

    const params = {
      productId: this.product.id,
      productTitle: this.querySelector(
        '.js-product-card-title .js-product-card-link'
      ).innerHTML,
      sku: selectedItem?.sku,
      title: selectedItem?.title,
      image: selectedItem?.featured_image
        ? selectedItem?.featured_image.src
        : this.product.featured_image,
    }

    if (hexcode && hexcode?.value !== undefined) {
      params.hexCode = hexcode.value
    }
    if (swatchImageInput && swatchImageInput?.value) {
      params.swatchImage = swatchImageInput.value
    }
    this.fillNotifyMeModal(params)
  }

  fillNotifyMeModal({ image, productTitle, hexCode, swatchImage, sku, productId, title }) {
    const notifyMeImage = document.querySelector('.js-slideout-notify-me-image')
    const notifyMeTitle = document.querySelector('.js-slideout-notify-me-title')
    const notifyMeVariant = document.querySelector(
      '.js-slideout-notify-me-variant'
    )
    const notifyMeSku = document.querySelector('#slideout-notify-me-sku')
    const notifyMeProductId = document.querySelector(
      '#slideout-notify-me-product-id'
    )

    notifyMeImage.innerHTML = `<img src="${image}" alt="${productTitle}" />`
    notifyMeTitle.innerHTML = productTitle
    if (title.toLowerCase() === 'default title') {
      notifyMeVariant.innerHTML = ''
    } else {
      let swatchHTML = ''

      // Prioritize swatch image over hex codes
      if (swatchImage) {
        swatchHTML = `<span class="block" style="background-image: url(${swatchImage}); background-size: cover;"></span>`
      } else if (hexCode) {
        swatchHTML = `<span class="block" style="background-color: ${hexCode};"></span>`
      }

      notifyMeVariant.innerHTML = `${swatchHTML}${title}`
    }
    notifyMeSku.value = sku
    notifyMeProductId.value = productId
  }

  decodeProduct(id) {
    return JSON.parse(
      this.querySelector(`#card-product-data-${id}`).textContent
    )
  }

  _removeListeners() {
    if (this.quickShopToggle) {
      this.quickShopToggle.removeEventListener(
        'click',
        this._trackQuickShopToggle
      )
    }

    if (this.notifyMeToggle) {
      this.notifyMeToggle.removeEventListener('click', this._notifyMeToggle)
    }

    if (this.inCartToggle) {
      this.inCartToggle.removeEventListener('click', this._handleInCartToggle)
    }
  }

  async _handleInCartToggle(e) {
    try {
      e.preventDefault()
      this.classList.add('busy')
      const cartLineItemKey = e.currentTarget.dataset.lineItemKey
      const cartItemsToRemove = (e.currentTarget.dataset.itemsToRemove || '')
        .split(',')
        .filter(Boolean)
      const property = this.inCartToggle.dataset.lineProperty
      if (cartItemsToRemove.length) {
        await window.__CART__CONTROLLER__.update(
          cartItemsToRemove.reduce((acc, item) => {
            acc[item] = 0
            return acc
          }, {})
        )
      }
      if (cartLineItemKey) {
        await window.__CART__CONTROLLER__.update({
          [cartLineItemKey]: 0,
        })
      } else {
        await window.__CART__CONTROLLER__.add([
          {
            id: this.inCartToggle.dataset.value,
            quantity: 1,
            ...(property ? { properties: { [property]: true } } : {}),
          },
        ])

        this.triggerEvent('click::add-to-cart', {
          product_id: this.dataset.id,
          variant_id: this.inCartToggle.dataset.value,
        })
      }

      this.triggerGlobalEvent('main-cart::refresh')
    } catch (error) {
      console.error(error)
      this.classList.remove('busy')
    }
  }
}
window.customElements.define('product-card', ProductCard)
